import React, { useState, useEffect, createContext, useContext } from "react";
import ReactDOM from "react-dom/client";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import App from "./App";
// Lazy: the no-auth prototype page (and the big command-center.css it pulls
// in) must not ride in the main bundle for every real user.
const CommandCenterPreview = React.lazy(() => import("./pages/CommandCenterPreview"));
import { setTokenGetter } from "./utils/api";
import {
  clearCommandCenterPreview,
  enableCommandCenterPreview,
  initializeFlags,
} from "./config/featureFlags";
import "./theme.css";
import "./styles/hf-motion.css";

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const previewMode = new URLSearchParams(window.location.search).get("preview");
const isCommandCenterPrototype = previewMode === "command-center-prototype";
const isLiveCommandCenterPreview = previewMode === "command-center";

if (!CLERK_PUBLISHABLE_KEY && !isCommandCenterPrototype) {
  // Fail loud. A silent dev-key fallback (previous behavior) is how staging
  // ended up talking to two different Clerk instances at once.
  throw new Error(
    "REACT_APP_CLERK_PUBLISHABLE_KEY is not set. Set it in the Vercel " +
      "project env to pk_live_… for clerk.optimus.university (prod). " +
      "There is no dev fallback anymore.",
  );
}

// Theme Context for app-wide theme management
const ThemeContext = createContext({
  theme: "auto",
  effectiveTheme: "dark",
  setTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

// Determine effective theme (dark or light) based on setting and system preference
const getEffectiveTheme = (themeSetting) => {
  if (themeSetting === "dark" || themeSetting === "light") {
    return themeSetting;
  }
  // Auto mode - check system preference
  if (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
};

// Apply theme to document
const applyTheme = (theme) => {
  // Always apply the effective theme (dark or light) to the DOM
  // CSS doesn't have [data-theme="auto"] styles, only dark/light
  const effectiveTheme = getEffectiveTheme(theme);
  document.documentElement.setAttribute("data-theme", effectiveTheme);
};

// Clerk dark theme appearance
const clerkDarkAppearance = {
  baseTheme: undefined,
  variables: {
    colorPrimary: "#3b82f6",
    colorText: "#ffffff",
    colorTextSecondary: "#e4e4e7",
    colorBackground: "#1c1c1e",
    colorInputBackground: "#2c2c2e",
    colorInputText: "#ffffff",
    colorDanger: "#ef4444",
    borderRadius: "8px",
  },
  elements: {
    // Facebook + LinkedIn SSO have no production credentials configured —
    // hide their buttons so users don't hit dead ends (Brad 2026-08-20:
    // 'either get it working, or take it off'). Google stays: being fixed
    // with real credentials.
    socialButtonsIconButton__facebook: { display: "none" },
    socialButtonsBlockButton__facebook: { display: "none" },
    socialButtonsIconButton__linkedin_oidc: { display: "none" },
    socialButtonsBlockButton__linkedin_oidc: { display: "none" },
    socialButtonsIconButton__linkedin: { display: "none" },
    socialButtonsBlockButton__linkedin: { display: "none" },

    rootBox: { backgroundColor: "#1c1c1e" },
    card: {
      backgroundColor: "#1c1c1e",
      border: "1px solid #38383a",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
    },
    modalContent: { backgroundColor: "#1c1c1e" },
    modalBackdrop: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
    navbar: { backgroundColor: "#1c1c1e", borderRight: "1px solid #38383a" },
    navbarButton: { color: "#e4e4e7" },
    navbarButtonIcon: { color: "#e4e4e7" },
    navbarMobileMenuButton: { color: "#ffffff" },
    pageScrollBox: { backgroundColor: "#1c1c1e" },
    page: { backgroundColor: "#1c1c1e" },
    headerTitle: { color: "#ffffff" },
    headerSubtitle: { color: "#a1a1aa" },
    profileSectionHeader: {
      backgroundColor: "#1c1c1e",
      borderBottom: "1px solid #38383a",
    },
    profileSectionHeaderTitle: {
      background: "none",
      backgroundColor: "transparent",
    },
    profileSection: { borderBottom: "1px solid #38383a" },
    profileSectionTitle: { color: "#a1a1aa", borderBottom: "none" },
    profileSectionTitleText: { color: "#a1a1aa" },
    profileSectionContent: { color: "#ffffff" },
    profileSectionPrimaryButton: { color: "#3b82f6" },
    formFieldLabel: { color: "#e4e4e7" },
    formFieldInput: {
      backgroundColor: "#2c2c2e",
      borderColor: "#38383a",
      color: "#ffffff",
    },
    formButtonPrimary: { backgroundColor: "#3b82f6", color: "#ffffff" },
    badge: { backgroundColor: "#3b82f6", color: "#ffffff" },
    accordionTriggerButton: { color: "#ffffff" },
    accordionContent: { backgroundColor: "#1c1c1e" },
    activeDeviceListItem: { borderBottom: "1px solid #38383a" },
    buttonArrowIcon: { color: "#e4e4e7" },
    actionCard: { backgroundColor: "#2c2c2e", border: "1px solid #38383a" },
    userButtonPopoverCard: {
      backgroundColor: "#1c1c1e",
      border: "1px solid #38383a",
    },
    userButtonPopoverActionButton: { color: "#ffffff" },
    userButtonPopoverActionButtonText: { color: "#ffffff" },
    userButtonPopoverActionButtonIcon: { color: "#e4e4e7" },
    userButtonPopoverFooter: { display: "none" },
    footer: { display: "none" },
    footerAction: { display: "none" },
    internal: { backgroundColor: "#1c1c1e" },
  },
};

// Custom Clerk error messages — replace generic messages with actionable ones
const clerkLocalization = {
  unstable__errors: {
    form_identifier_not_found:
      "No account found for this email. If you purchased a subscription, check your inbox for an invitation link from Optimus OS.",
    form_identifier_exists__email_address:
      "An account with this email already exists. Try signing in instead.",
    form_identifier_exists__phone_number:
      "This phone number is already linked to another account. Please use a different number.",
    form_identifier_exists__username:
      "This username is already taken. Please choose a different one.",
    not_allowed_access:
      "Access restricted. You need an invitation to sign up. Visit activateoptimus.com to purchase a subscription.",
    form_password_pwned__sign_in:
      "This password was found in a data breach. Please reset your password using 'Forgot password'.",
  },
};

// Clerk light theme appearance
const clerkLightAppearance = {
  baseTheme: undefined,
  variables: {
    colorPrimary: "#007aff",
    colorText: "#1c1c1e",
    colorTextSecondary: "#3c3c43",
    colorBackground: "#ffffff",
    colorInputBackground: "#f5f5f7",
    colorInputText: "#1c1c1e",
    colorDanger: "#ff3b30",
    borderRadius: "8px",
  },
  elements: {
    // Facebook + LinkedIn SSO have no production credentials configured —
    // hide their buttons so users don't hit dead ends (Brad 2026-08-20:
    // 'either get it working, or take it off'). Google stays: being fixed
    // with real credentials.
    socialButtonsIconButton__facebook: { display: "none" },
    socialButtonsBlockButton__facebook: { display: "none" },
    socialButtonsIconButton__linkedin_oidc: { display: "none" },
    socialButtonsBlockButton__linkedin_oidc: { display: "none" },
    socialButtonsIconButton__linkedin: { display: "none" },
    socialButtonsBlockButton__linkedin: { display: "none" },

    rootBox: { backgroundColor: "#ffffff" },
    card: {
      backgroundColor: "#ffffff",
      border: "1px solid #d1d1d6",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    },
    modalContent: { backgroundColor: "#ffffff" },
    modalBackdrop: { backgroundColor: "rgba(0, 0, 0, 0.4)" },
    navbar: { backgroundColor: "#f5f5f7", borderRight: "1px solid #d1d1d6" },
    navbarButton: { color: "#3c3c43" },
    navbarButtonIcon: { color: "#3c3c43" },
    navbarMobileMenuButton: { color: "#1c1c1e" },
    pageScrollBox: { backgroundColor: "#ffffff" },
    page: { backgroundColor: "#ffffff" },
    headerTitle: { color: "#1c1c1e" },
    headerSubtitle: { color: "#8e8e93" },
    profileSectionHeader: {
      backgroundColor: "#ffffff",
      borderBottom: "1px solid #d1d1d6",
    },
    profileSectionHeaderTitle: {
      background: "none",
      backgroundColor: "transparent",
    },
    profileSection: { borderBottom: "1px solid #d1d1d6" },
    profileSectionTitle: { color: "#8e8e93", borderBottom: "none" },
    profileSectionTitleText: { color: "#8e8e93" },
    profileSectionContent: { color: "#1c1c1e" },
    profileSectionPrimaryButton: { color: "#007aff" },
    formFieldLabel: { color: "#3c3c43" },
    formFieldInput: {
      backgroundColor: "#f5f5f7",
      borderColor: "#d1d1d6",
      color: "#1c1c1e",
    },
    formButtonPrimary: { backgroundColor: "#007aff", color: "#ffffff" },
    badge: { backgroundColor: "#007aff", color: "#ffffff" },
    accordionTriggerButton: { color: "#1c1c1e" },
    accordionContent: { backgroundColor: "#ffffff" },
    activeDeviceListItem: { borderBottom: "1px solid #d1d1d6" },
    buttonArrowIcon: { color: "#3c3c43" },
    actionCard: { backgroundColor: "#f5f5f7", border: "1px solid #d1d1d6" },
    userButtonPopoverCard: {
      backgroundColor: "#ffffff",
      border: "1px solid #d1d1d6",
    },
    userButtonPopoverActionButton: { color: "#1c1c1e" },
    userButtonPopoverActionButtonText: { color: "#1c1c1e" },
    userButtonPopoverActionButtonIcon: { color: "#3c3c43" },
    userButtonPopoverFooter: { display: "none" },
    footer: { display: "none" },
    footerAction: { display: "none" },
    internal: { backgroundColor: "#ffffff" },
  },
};

// Theme Provider Component
function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(
    localStorage.getItem("themeMode") || "dark",
  );
  const [effectiveTheme, setEffectiveTheme] = useState(
    getEffectiveTheme(theme),
  );

  const setTheme = (newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem("themeMode", newTheme);
    applyTheme(newTheme);
    setEffectiveTheme(getEffectiveTheme(newTheme));
  };

  // Initialize theme on mount
  useEffect(() => {
    applyTheme(theme);
    setEffectiveTheme(getEffectiveTheme(theme));
  }, []);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (!window.matchMedia) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (theme === "auto") {
        setEffectiveTheme(getEffectiveTheme("auto"));
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  // Expose setTheme globally for components that can't use context
  useEffect(() => {
    window.applyTheme = setTheme;
  }, []);

  const clerkAppearance =
    effectiveTheme === "dark" ? clerkDarkAppearance : clerkLightAppearance;

  return (
    <ThemeContext.Provider value={{ theme, effectiveTheme, setTheme }}>
      <ClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY}
        appearance={clerkAppearance}
        localization={clerkLocalization}
      >
        {children}
      </ClerkProvider>
    </ThemeContext.Provider>
  );
}

// Wrapper component to connect Clerk auth to API utility
function AuthConnector({ children }) {
  const { getToken } = useAuth();

  React.useEffect(() => {
    // Connect Clerk's getToken to our API utility
    // Always use skipCache to get fresh tokens, never expired cached ones
    // Short-TTL token cache. skipCache:true on EVERY call minted a fresh
    // Clerk token per request — busy screens (ROI, Team) tripped Clerk's
    // rate limiter (HTTP 429 storms observed in the 2026-08-25 playtest
    // crawl: 20 failed token requests on one surface). Clerk tokens live
    // ~60s; caching for 30s keeps them fresh while collapsing the storm,
    // and any 401 path still re-mints (skipCache) on demand.
    let _tok = null;
    let _tokAt = 0;
    const TOKEN_TTL_MS = 30000;
    setTokenGetter(async (opts = {}) => {
      const now = Date.now();
      if (!opts.force && _tok && now - _tokAt < TOKEN_TTL_MS) return _tok;
      const fresh = await getToken({ skipCache: true });
      _tok = fresh;
      _tokAt = now;
      return fresh;
    });
  }, [getToken]);

  return children;
}

// App wrapper with auth connector
function AppWithAuth() {
  return (
    <AuthConnector>
      <App />
    </AuthConnector>
  );
}

// Initialize feature flags BEFORE React renders
// This prevents race conditions where components check flags before they're loaded
const root = ReactDOM.createRoot(document.getElementById("root"));

if (isCommandCenterPrototype) {
  applyTheme("dark");
  root.render(
    <React.Suspense fallback={null}>
      <CommandCenterPreview />
    </React.Suspense>,
  );
} else {
  if (isLiveCommandCenterPreview) enableCommandCenterPreview();
  if (previewMode === "command-center-reset") clearCommandCenterPreview();
  initializeFlags()
  .then(() => {
    console.log("[App] Feature flags initialized, rendering app");
    root.render(
      <ThemeProvider>
        <AppWithAuth />
      </ThemeProvider>,
    );
  })
  .catch((error) => {
    console.error("[App] Feature flag init failed, rendering anyway:", error);
    root.render(
      <ThemeProvider>
        <AppWithAuth />
      </ThemeProvider>,
    );
  });
}

// Suppress ResizeObserver errors (common React issue)
window.addEventListener("error", (e) => {
  if (
    e.message ===
    "ResizeObserver loop completed with undelivered notifications."
  ) {
    const resizeObserverErrDiv = document.getElementById(
      "webpack-dev-server-client-overlay-div",
    );
    const resizeObserverErr = document.getElementById(
      "webpack-dev-server-client-overlay",
    );
    if (resizeObserverErr) {
      resizeObserverErr.setAttribute("style", "display: none");
    }
    if (resizeObserverErrDiv) {
      resizeObserverErrDiv.setAttribute("style", "display: none");
    }
  }
});
